import { useEffect, useState } from "react";
import { sb } from "@/lib/sb";
import { SitePhoto } from "@/components/SitePhoto";
import { SafeImg } from "@/components/SafeImg";
import { cn } from "@/lib/utils";
import { resolvePublicMedia } from "@/lib/public-media.functions";
import { buildSrcSet, pickSrc, type VariantMap } from "@/lib/image-srcset";

// One place on the site that can be edited as a slideshow from the dashboard.
// If a slideshow has been configured for `slideshowKey` it rotates through
// media-library photos; otherwise it falls back to the legacy single-slot
// site_photos image(s) so the layout never breaks.
interface SlideshowSpotProps {
  slideshowKey: string;
  fallbackSlotKeys: string[];
  label: string;
  className?: string;
  kenBurns?: boolean;
  /** Render the first image eagerly with fetchpriority="high". Use for above-the-fold hero. */
  priority?: boolean;
  /** Static image shown instantly behind the slideshow until slides resolve. */
  fallbackSrc?: string;
  /**
   * Responsive `sizes` for the rendered <img>. Defaults to 100vw. Callers
   * should override with actual per-breakpoint widths.
   */
  sizes?: string;
}


type DbSlide = { mediaId: string; alt?: string | null };
type DbSlideshow = { intervalMs: number; transitionMs: number; items: DbSlide[] };

type ResolvedSlide = { url: string; alt: string; variants: VariantMap };

// Cache the full slideshows map once per page load so every spot doesn't
// trigger its own settings round-trip.
let slideshowsMapPromise: Promise<Record<string, DbSlideshow>> | null = null;
function getSlideshowsMap(): Promise<Record<string, DbSlideshow>> {
  if (slideshowsMapPromise) return slideshowsMapPromise;
  const p = sb
    .from("public_app_settings")
    .select("slideshows")
    .eq("id", 1)
    .maybeSingle()
    .then(({ data, error }: { data: { slideshows: Record<string, DbSlideshow> } | null; error: { message: string } | null }) => {
      if (error) throw new Error(error.message);
      return data?.slideshows ?? {};
    })
    .catch((error: unknown) => {
      slideshowsMapPromise = null;
      throw error;
    });
  slideshowsMapPromise = p;
  return p;
}

// Cache media-library lookups too.
type MediaEntry = { url: string; alt: string; variants: VariantMap };
const mediaCache = new Map<string, MediaEntry>();
async function resolveMedia(ids: string[]): Promise<Map<string, MediaEntry>> {
  const out = new Map<string, MediaEntry>();
  const missing: string[] = [];
  for (const id of ids) {
    const hit = mediaCache.get(id);
    if (hit) out.set(id, hit);
    else missing.push(id);
  }
  if (missing.length) {
    const rows = await resolvePublicMedia({ data: { ids: missing } });
    for (const m of rows) {
      const entry: MediaEntry = { url: m.url, alt: m.alt, variants: (m as any).variants ?? null };
      mediaCache.set(m.id, entry);
      out.set(m.id, entry);
    }
  }
  return out;
}

// Injects a <link rel="preload" as="image" imagesrcset imagesizes> once for
// the first hero slide, replacing the coarse single-URL preload in
// __root.tsx with the width-optimal variant. Idempotent per URL.
const preloadedSrcSets = new Set<string>();
function injectHeroPreload(srcSet: string | undefined, src: string, sizes: string) {
  if (typeof document === "undefined") return;
  const key = srcSet || src;
  if (preloadedSrcSets.has(key)) return;
  preloadedSrcSets.add(key);
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  (link as any).fetchPriority = "high";
  if (srcSet) {
    (link as any).imageSrcset = srcSet;
    (link as any).imageSizes = sizes;
  } else {
    link.href = src;
  }
  document.head.appendChild(link);
}

export function SlideshowSpot({
  slideshowKey,
  fallbackSlotKeys,
  label,
  className,
  kenBurns = false,
  priority = false,
  fallbackSrc,
  sizes = "100vw",
}: SlideshowSpotProps) {

  const [slides, setSlides] = useState<ResolvedSlide[]>([]);
  const [intervalMs, setIntervalMs] = useState(5000);
  const [transitionMs, setTransitionMs] = useState(1000);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [firstSlideReady, setFirstSlideReady] = useState(false);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const markFailed = (url: string) =>
    setFailedUrls((prev) => (prev.has(url) ? prev : new Set(prev).add(url)));


  useEffect(() => {
    let cancelled = false;
    let retryId: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const map = await getSlideshowsMap();
        const cfg = map[slideshowKey];
        if (!cfg || !cfg.items?.length) {
          if (!cancelled) setLoaded(true);
          return;
        }
        const ids = cfg.items.map((i) => i.mediaId);
        const byId = await resolveMedia(ids);
        const built = cfg.items
          .map((i) => {
            const m = byId.get(i.mediaId);
            if (!m) return null;
            return { url: m.url, alt: i.alt || m.alt || label, variants: m.variants };
          })
          .filter(Boolean) as ResolvedSlide[];
        if (cancelled) return;
        setSlides(built);
        setIntervalMs(cfg.intervalMs || 5000);
        setTransitionMs(cfg.transitionMs ?? 1000);
        setLoaded(true);

        if (priority && built[0]) {
          const first = built[0];
          const srcSet = buildSrcSet(first.variants);
          const src = pickSrc(first.variants, first.url);
          injectHeroPreload(srcSet, src, sizes);
        }
      } catch {
        if (!cancelled) retryId = setTimeout(() => void load(), 3000);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (retryId) clearTimeout(retryId);
    };
  }, [slideshowKey, label, priority, sizes]);

  const visibleSlides = slides.filter((s) => !failedUrls.has(s.url));

  useEffect(() => {
    if (visibleSlides.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % visibleSlides.length), intervalMs);
    return () => clearInterval(id);
  }, [visibleSlides.length, intervalMs]);

  const safeIdx = visibleSlides.length > 0 ? idx % visibleSlides.length : 0;

  // Slideshow configured and at least one slide still loading successfully.
  if (visibleSlides.length > 0) {
    const zoomDuration = intervalMs + transitionMs;
    return (
      <div className={cn("relative w-full h-full overflow-hidden", className)}>
        {/* Instant fallback behind slides; fades out once first slide has decoded */}
        {fallbackSrc && (
          <img
            src={fallbackSrc}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            style={{ opacity: firstSlideReady ? 0 : 1 }}
          />
        )}
        {visibleSlides.map((s, i) => {
          const active = i === safeIdx;
          const isFirst = i === 0;
          const srcSet = buildSrcSet(s.variants);
          const src = pickSrc(s.variants, s.url);
          return (
            <div
              key={s.url}
              className="absolute inset-0"
              style={{
                opacity: active ? 1 : 0,
                transition: `opacity ${transitionMs}ms ease-in-out`,
              }}
              aria-hidden={!active}
            >
              <SafeImg
                src={src}
                {...(srcSet ? { srcSet, sizes } : {})}
                alt={s.alt}
                width={1920}
                height={1080}
                className="w-full h-full object-cover"
                loading={priority && isFirst ? "eager" : "lazy"}
                decoding={priority && isFirst ? "sync" : "async"}
                {...(priority && isFirst ? { fetchPriority: "high" as const } : {})}
                onLoad={isFirst ? () => setFirstSlideReady(true) : undefined}
                onFail={() => markFailed(s.url)}
                style={
                  kenBurns
                    ? {
                        transformOrigin: "center",
                        animation: `ken-burns ${zoomDuration}ms linear infinite alternate`,
                      }
                    : undefined
                }
              />

            </div>
          );
        })}
      </div>
    );
  }


  // Slides haven't resolved yet — show the static fallback immediately if provided.
  if (!loaded && fallbackSrc) {
    return (
      <div className={cn("relative w-full h-full overflow-hidden", className)}>
        <img
          src={fallbackSrc}
          alt={label}
          width={1920}
          height={1080}
          className="w-full h-full object-cover"
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          {...(priority ? { fetchPriority: "high" as const } : {})}
        />
      </div>
    );
  }

  // Not configured yet — fall back to the existing single-slot photo so the
  // layout never breaks. (For home.hero the first slot is used.)
  const slotKey = fallbackSlotKeys[0];
  return <SitePhoto slotKey={slotKey} label={label} className={className} sizes={sizes} />;
}
